import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object;
  noindex?: boolean;
  city?: string;
  state?: string;
}

export default function SEOHead({
  title = "UCX: #1 Managed IT Services Provider in Texas | Dallas MSP | Enterprise IT Solutions",
  description = "UCX is Texas' leading managed IT services provider (MSP) serving Dallas, Fort Worth, Austin, Houston & all of Texas. 24/7 IT support, cloud solutions, network security. Call 844-219-3935 for enterprise IT services.",
  keywords = "managed IT services Texas, Dallas MSP, managed service provider Dallas, IT support Dallas, managed IT services Dallas, Fort Worth MSP, Austin managed IT, Houston IT services, Texas IT company",
  canonical = "https://ucx.co/",
  ogImage = "https://i.ibb.co/QpjHXgM/UCXLogo.png",
  ogType = "website",
  structuredData,
  noindex = false,
  city,
  state = "Texas"
}: SEOHeadProps) {
  
  const citySpecificTitle = city ? 
    `${title.replace("Texas", city)} | ${city} Managed IT Services` : 
    title;
    
  const citySpecificDescription = city ?
    description.replace("Dallas, Fort Worth, Austin, Houston & all of Texas", `${city} and surrounding areas in Texas`) :
    description;

  const citySpecificKeywords = city ?
    `${keywords}, ${city} IT services, ${city} managed IT, ${city} MSP, ${city} IT support, ${city} IT company` :
    keywords;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{citySpecificTitle}</title>
      <meta name="description" content={citySpecificDescription} />
      <meta name="keywords" content={citySpecificKeywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="US-TX" />
      {city && <meta name="geo.placename" content={`${city}, ${state}`} />}
      <meta name="geo.country" content="US" />
      <meta name="geo.state" content={state} />
      {city && <meta name="geo.city" content={city} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={citySpecificTitle} />
      <meta property="og:description" content={citySpecificDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="UCX" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={citySpecificTitle} />
      <meta name="twitter:description" content={citySpecificDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@ConneXionsDFW" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}